import { AxiosClient } from "./AxiosClient";

const isII = process.env.REACT_APP_IS_II === "true";
const isAB = process.env.REACT_APP_IS_AB === "true";

const TARGET_TYPES = isII
  ? [{ key: "daily_report", default_title: "" }]
  : [
      { key: "daily_report", default_title: "" },
      { key: "company_profile", default_title: "Company Profile" },
      { key: "customers", default_title: "" },
      { key: "market_size", default_title: "" },
      { key: "products", default_title: "" },
      { key: "ip", default_title: "" },
      { key: "complaints", default_title: "" },
      { key: "financial", default_title: "" },
      { key: "trends", default_title: "" },
      { key: "partners", default_title: "" },
      { key: "executives", default_title: "" },
      { key: "employees", default_title: "" },
      { key: "location", default_title: "" },
      { key: "neightbors", default_title: "" },
      { key: "certs", default_title: "" },
    ];

const getReportTargetTypes = async (target) => {
  if (!isAB) return TARGET_TYPES;

  const response = await getAvailableTargetTypesAB(target);

  if (!Array.isArray(response?.data)) return [];

  return response.data.map((targetType) => ({
    key: targetType,
    default_title: "",
  }));
};

const fetchReportsForTargetTypes = async (target, targetTypes) => {
  if (!Array.isArray(targetTypes) || targetTypes.length === 0) {
    return { data: [] };
  }

  const reportPromises = targetTypes.map((targetType) =>
    AxiosClient.post("wiki/getReports", {
      target,
      target_type: targetType.key,
    }),
  );

  const results = await Promise.allSettled(reportPromises);

  const allReports = results.reduce((acc, result, index) => {
    if (result.status !== "fulfilled") return acc;

    const { key, default_title } = targetTypes[index];
    const versions = result.value?.data;

    if (
      Array.isArray(versions) &&
      versions.length > 0 &&
      versions[0].content != null
    ) {
      acc.push({
        ...versions[0],
        target_type: key,
        title: versions[0].section_title || default_title,
        versions,
      });
    }

    return acc;
  }, []);

  return { data: allReports };
};

export const getAvailableWikiData = async (company_name) => {
  try {
    const results = await Promise.allSettled([
      AxiosClient.post("wiki/getCompanyProfileIcon", { company_name }),
      AxiosClient.post("wiki/getCompanyEmployeesIcon", { company_name }),
      AxiosClient.post("wiki/getCourtCasesIcon", { company_name }),
      AxiosClient.post("wiki/getNeighborsIcon", { company_name }),
      AxiosClient.post("wiki/getGovAwardsIcon", { company_name }),
      AxiosClient.post("wiki/getDomainCertsIcon", { company_name }),
      AxiosClient.post("wiki/getBolsIcon", { company_name }),
      AxiosClient.post("wiki/getSanctionLookupIcon", { company_name }),
      AxiosClient.get("wiki/getSitemapUrlIcon", { params: { company_name } }),
      AxiosClient.post("wiki/getCrypto", { company_name }),
      isAB
        ? AxiosClient.post("wiki/getAvailableTargetTypesAdmiralBlue", {
            company_name,
          })
        : AxiosClient.post("wiki/getAvailableTargetTypes", { company_name }),
      AxiosClient.post("wiki/getDomainInfo", { company_name }),
      AxiosClient.post("wiki/getMld", { company_name }),
    ]);

    const getData = (index) => {
      const result = results[index];

      if (
        result.status === "fulfilled" &&
        result.value?.data &&
        result.value.data !== "fail"
      ) {
        return result.value.data;
      }

      return null;
    };

    const profile = getData(0);
    const employees = getData(1);
    const cases = getData(2);
    const neighbors = getData(3);
    const gov = getData(4);
    const domainsCerts = getData(5);
    const bols = getData(6);
    const sanctions = getData(7);
    const site = getData(8);
    const crypto = getData(9);
    const reportTypes = getData(10);
    const domainIPs = getData(11);
    const mld = getData(12);

    const response_array = [];

    response_array.push({
      sub_section: "Overview",
      ...(profile ? { data: profile } : {}),
    });

    if (employees && Array.isArray(employees) && employees.length > 0) {
      response_array.push({ sub_section: "Key Employees", data: employees });
    }

    if (cases && cases.count > 0) {
      response_array.push({ sub_section: "Court Cases", data: cases });
    }

    if (neighbors && Array.isArray(neighbors) && neighbors.length > 0) {
      response_array.push({ sub_section: "Neighbors", data: neighbors });
    }

    if (gov && gov.count > 0) {
      response_array.push({ sub_section: "Gov Awards", data: gov });
    }

    if (domainIPs && domainIPs.count > 0) {
      response_array.push({ sub_section: "domainIPs", data: domainIPs });
    }

    if (
      domainsCerts &&
      Array.isArray(domainsCerts) &&
      domainsCerts.length > 0
    ) {
      const transformedData = domainsCerts.map((item) => ({
        title: item.name_value,
        value: item.issuer_ca_id,
      }));

      response_array.push({ sub_section: "Domains", data: transformedData });
    }

    if (
      bols &&
      Array.isArray(bols) &&
      bols.length > 0 &&
      bols.find((item) => item.bol_count)?.bol_count > 0
    ) {
      response_array.push({ sub_section: "BOLs", data: bols });
    }

    if (sanctions && sanctions.count > 0) {
      response_array.push({ sub_section: "Sanctions", data: sanctions });
    }

    if (site && site.count > 0) {
      response_array.push({ sub_section: "Site Map", data: site });
    }

    if (crypto && Array.isArray(crypto) && crypto.length > 0) {
      response_array.push({ sub_section: "Crypto", data: crypto });
    }

    if (reportTypes && Array.isArray(reportTypes) && reportTypes.length > 0) {
      response_array.push({
        sub_section: "Reports",
        data: { count: reportTypes.length },
      });
    }

    if (mld && Array.isArray(mld) && mld.length > 0) {
      response_array.push({
        sub_section: "MLD",
        data: { count: mld.length },
      });
    }

    return response_array;
  } catch (err) {
    console.error("Error fetching wiki data:", err);
    return [];
  }
};

export const getCompanyEmployees = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getCompanyEmployees", { company_name });
  } catch (error) {
    console.error("Error fetching company employees:", error);
    return { data: [] };
  }
};

export const getCourtCases = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getCourtCases", { company_name });
  } catch (error) {
    console.error("Error fetching company court cases:", error);
    return { data: [] };
  }
};

export const getNeighbors = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getNeighbors", { company_name });
  } catch (error) {
    console.error("Error fetching company getNeighbors:", error);
    return { data: [] };
  }
};

export const getDomainCerts = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getDomainCerts", { company_name });
  } catch (error) {
    console.error("Error fetching company getDomainCerts:", error);
    return { data: [] };
  }
};

export const getSanctions = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getSanctionLookup", { company_name });
  } catch (error) {
    console.error("Error fetching company getSanctions:", error);
    return { data: [] };
  }
};

export const getCrypto = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getCrypto", { company_name });
  } catch (error) {
    console.error("Error fetching company getCrypto:", error);
    return { data: [] };
  }
};

export const getDomainInfo = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getDomainInfo", { company_name });
  } catch (error) {
    console.error("Error fetching company getDomainInfo:", error);
    return { data: [] };
  }
};

export const getAvailableTargetTypes = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getAvailableTargetTypes", {
      company_name,
    });
  } catch (error) {
    console.error("Error fetching available target types:", error);
    return { data: [] };
  }
};

export const getAvailableTargetTypesAB = async (target) => {
  try {
    return await AxiosClient.post("wiki/getAvailableTargetTypesAdmiralBlue", {
      target,
    });
  } catch (error) {
    console.error("Error fetching AB available target types:", error);
    return { data: [] };
  }
};

export const getReportsII = async () => {
  try {
    const response = await AxiosClient.post("wiki/getReports", {
      target_type: "daily_report",
    });

    const reports = response?.data;

    return { data: Array.isArray(reports) ? reports : [] };
  } catch (error) {
    console.error("Error fetching II reports:", error);
    return { data: [] };
  }
};

export const getReports = async (target) => {
  try {
    const targetTypes = await getReportTargetTypes(target);
    console.log({ targetTypes });
    return await fetchReportsForTargetTypes(target, targetTypes);
  } catch (error) {
    console.error("Error fetching reports:", error);
    return { data: [] };
  }
};

export const getBols = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getBols", { company_name });
  } catch (error) {
    console.error("Error fetching company getBols:", error);
    return { data: [] };
  }
};

export const getMld = async (company_name) => {
  try {
    return await AxiosClient.post("wiki/getMld", { company_name });
  } catch (error) {
    console.error("Error fetching company getMld:", error);
    return { data: [] };
  }
};
